import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, Trash2, Plus, Inbox, Loader2, ImageIcon, Wand2, AlertTriangle, Star } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Admin() {
  const [auth, setAuth] = useState("checking");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [logging, setLogging] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [rates, setRates] = useState([]);
  const [week, setWeek] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [b, r, p] = await Promise.all([
        ax.get(`${API}/admin/bookings`),
        ax.get(`${API}/admin/rates`),
        ax.get(`${API}/admin/gallery`),
      ]);
      setBookings(b.data);
      setRates(r.data);
      setPhotos(p.data);
      setAuth("ok");
    } catch (e) {
      if (e.response?.status === 401) {
        try {
          await ax.post(`${API}/auth/refresh`);
          const [b, r, p] = await Promise.all([
            ax.get(`${API}/admin/bookings`),
            ax.get(`${API}/admin/rates`),
            ax.get(`${API}/admin/gallery`),
          ]);
          setBookings(b.data);
          setRates(r.data);
          setPhotos(p.data);
          setAuth("ok");
          return;
        } catch (_) {}
      }
      setAuth("login");
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await ax.post(`${API}/admin/upload`, fd);
      const { data } = await ax.get(`${API}/admin/gallery`);
      setPhotos(data);
      toast.success("Foto aggiunta alla galleria");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const movePhoto = async (id, dir) => {
    const arr = [...photos];
    const i = arr.findIndex((p) => p.id === id);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPhotos(arr);
    try {
      const { data } = await ax.post(`${API}/admin/gallery/reorder`, { ids: arr.map((p) => p.id) });
      setPhotos(data);
    } catch (err) {
      toast.error("Ordine non salvato");
    }
  };

  const delPhoto = async (id) => {
    try {
      await ax.delete(`${API}/admin/gallery/${id}`);
      setPhotos((ps) => ps.filter((p) => p.id !== id));
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const genImage = async (e) => {
    e.preventDefault();
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      await ax.post(`${API}/admin/generate-image`, { prompt: aiPrompt.trim() });
      const { data } = await ax.get(`${API}/admin/gallery`);
      setPhotos(data);
      setAiPrompt("");
      toast.success("Immagine generata e aggiunta");
    } catch (err) {
      toast.error("Generazione non riuscita");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash.includes("session_id=")) return; // Google OAuth callback gestito sotto
    ax.get(`${API}/auth/me`)
      .then(() => load())
      .catch(() => setAuth("login"));
  }, []);

  const location = useLocation();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (!location.hash.includes("session_id=")) return;
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sid = location.hash.split("session_id=")[1].split("&")[0];
    ax.post(`${API}/auth/session`, { session_id: sid })
      .then(() => load())
      .catch((e) => {
        setLoginErr(e.response?.status === 403 ? "Email non autorizzata per questo pannello" : "Accesso Google non riuscito");
        setAuth("login");
      });
  }, [location.hash]);

  const login = async (e) => {
    e.preventDefault();
    setLogging(true);
    setLoginErr("");
    try {
      const { data } = await ax.post(`${API}/auth/login`, { email, password });
      setUser(data.user);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginErr(typeof d === "string" ? d : "Accesso non riuscito");
    } finally {
      setLogging(false);
    }
  };

  const logout = async () => {
    try {
      await ax.post(`${API}/auth/logout`);
    } finally {
      setAuth("login");
      setUser(null);
    }
  };

  const addRate = async (e) => {
    e.preventDefault();
    if (!week || !price) return;
    setBusy(true);
    try {
      await ax.post(`${API}/admin/rates`, { week_start: week, price: Number(price) });
      const { data } = await ax.get(`${API}/admin/rates`);
      setRates(data);
      setWeek("");
      setPrice("");
      toast.success("Tariffa salvata");
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const delRate = async (id) => {
    try {
      await ax.delete(`${API}/admin/rates/${id}`);
      setRates((rs) => rs.filter((r) => r.id !== id));
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const input = "h-11 w-full rounded-md border border-sand/20 bg-ink px-3.5 text-sm text-sand outline-none placeholder:text-sand/40 focus:border-terra";

  if (auth === "checking") {
    return (
      <div className="grid min-h-screen place-items-center bg-ink text-sand">
        <Loader2 className="h-6 w-6 animate-spin text-terra" data-testid="admin-loading" />
      </div>
    );
  }

  if (auth !== "ok") {
    return (
      <div className="grid min-h-screen place-items-center bg-ink px-5 text-sand">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-lg border border-sand/10 bg-ink2/30 p-8">
          <div className="mb-6 flex items-center gap-3">
            <LogoMark dark className="h-9 w-9" />
            <div>
              <p className="font-serif text-xl">Area proprietario</p>
              <p className="text-xs uppercase tracking-[0.25em] text-sand/60">Private Oasis Villa</p>
            </div>
          </div>
          <div className="grid gap-4">
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className={input}
            />
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={input}
            />
            {loginErr && (
              <p data-testid="admin-login-error" className="text-sm text-terra">
                {loginErr}
              </p>
            )}
            <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-sand/40">
              <span className="h-px flex-1 bg-sand/15" /> oppure <span className="h-px flex-1 bg-sand/15" />
            </div>
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              data-testid="admin-google-login"
              onClick={() => {
                const redirectUrl = window.location.origin + "/admin";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="flex h-11 w-full items-center justify-center gap-3 rounded-full border border-sand/25 text-sm font-semibold text-sand transition-colors duration-300 hover:bg-sand hover:text-ink"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4">
                <path fill="#EA4335" d="M12 5.04c1.7 0 3.22.59 4.42 1.73l3.29-3.29C17.73 1.64 15.06.5 12 .5 7.44.5 3.5 3.12 1.5 6.93l3.85 2.99C6.27 7.09 8.86 5.04 12 5.04z" />
                <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.72h6.45c-.29 1.48-1.14 2.73-2.41 3.57l3.72 2.89c2.17-2 3.74-4.96 3.74-8.73z" />
                <path fill="#FBBC05" d="M5.35 14.08a7.06 7.06 0 0 1 0-4.16L1.5 6.93A11.49 11.49 0 0 0 .5 12c0 1.85.44 3.6 1.24 5.07l3.61-2.99z" />
                <path fill="#34A853" d="M12 23.5c3.06 0 5.63-1.01 7.5-2.75l-3.57-2.77c-1 .67-2.27 1.07-3.93 1.07-3.14 0-5.73-2.05-6.65-4.88l-3.85 2.99C3.5 20.88 7.44 23.5 12 23.5z" />
              </svg>
              Continua con Google
            </button>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={logging}
              className="flex h-11 items-center justify-center gap-2 rounded-full bg-terra text-sm font-semibold uppercase tracking-[0.14em] text-sand transition-colors duration-300 hover:bg-olive disabled:opacity-60"
            >
              {logging && <Loader2 className="h-4 w-4 animate-spin" />}
              Accedi
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-5 py-10 text-sand md:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-sand/10 pb-6">
          <div className="flex items-center gap-3">
            <LogoMark dark className="h-9 w-9" />
            <div>
              <p className="font-serif text-2xl">Pannello richieste</p>
              <p className="text-xs uppercase tracking-[0.25em] text-sand/60">{user?.email || email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-back-site" className="rounded-full border border-sand/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] transition-colors duration-300 hover:bg-sand hover:text-ink">
              Torna al sito
            </a>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] transition-colors duration-300 hover:bg-olive"
            >
              <LogOut className="h-4 w-4" />
              Esci
            </button>
          </div>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <section className="lg:col-span-2">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl">
              <Inbox className="h-5 w-5 text-terra" />
              Richieste di prenotazione ({bookings.length})
            </h2>
            {bookings.length === 0 ? (
              <p className="rounded-lg border border-dashed border-sand/20 p-8 text-center text-sm text-sand/60" data-testid="admin-bookings-empty">
                Nessuna richiesta ricevuta (ancora).
              </p>
            ) : (
              <div className="grid gap-4" data-testid="admin-bookings-table">
                {bookings.map((b) => (
                  <article key={b.id} className="rounded-lg border border-sand/10 bg-ink2/25 p-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-serif text-xl">{b.name}</p>
                      <p className="text-xs text-sand/50">{fmtDate(b.created_at)}</p>
                    </div>
                    <p className="mt-1 text-sm text-sand/80">
                      {b.email}{b.phone ? ` · ${b.phone}` : ""}
                    </p>
                    {(b.check_in || b.check_out || b.guests) && (
                      <p className="mt-2 text-sm text-terra">
                        {b.check_in || "?"} → {b.check_out || "?"}{b.guests ? ` · ${b.guests} ospiti` : ""}
                      </p>
                    )}
                    {b.message && <p className="mt-2 text-sm italic text-sand/70">“{b.message}”</p>}
                    {(b.spam || b.priority) && (
                      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="admin-triage-chips">
                        {b.spam && (
                          <span className="flex items-center gap-1 rounded-full border border-terra/50 px-3 py-1 text-[10px] uppercase tracking-widest text-terra">
                            <AlertTriangle className="h-3 w-3" /> Possibile spam
                          </span>
                        )}
                        {b.priority && !b.spam && (
                          <span className="flex items-center gap-1 rounded-full border border-olive/60 px-3 py-1 text-[10px] uppercase tracking-widest text-olive">
                            <Star className="h-3 w-3" /> Priorità: {b.priority}
                          </span>
                        )}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-4 font-serif text-2xl">Tariffe settimanali</h2>
            <form onSubmit={addRate} className="rounded-lg border border-sand/10 bg-ink2/25 p-5" data-testid="admin-rate-form">
              <div className="grid gap-3">
                <label className="text-xs uppercase tracking-[0.2em] text-sand/60">Settimana (sabato)</label>
                <input
                  data-testid="admin-rate-date"
                  type="date"
                  required
                  value={week}
                  onChange={(e) => setWeek(e.target.value)}
                  className={input}
                />
                <label className="text-xs uppercase tracking-[0.2em] text-sand/60">Prezzo (€ a settimana)</label>
                <input
                  data-testid="admin-rate-price"
                  type="number"
                  min="0"
                  required
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="1200"
                  className={input}
                />
                <button
                  type="submit"
                  data-testid="admin-rate-add"
                  disabled={busy}
                  className="flex h-11 items-center justify-center gap-2 rounded-full bg-olive text-sm font-semibold uppercase tracking-[0.14em] text-sand transition-colors duration-300 hover:bg-terra disabled:opacity-60"
                >
                  <Plus className="h-4 w-4" />
                  Salva tariffa
                </button>
              </div>
            </form>
            <div className="mt-4 grid gap-2" data-testid="admin-rates-list">
              {rates.length === 0 && (
                <p className="text-sm text-sand/50">Nessuna tariffa impostata: il sito mostra “Su richiesta”.</p>
              )}
              {rates.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border border-sand/10 px-4 py-3">
                  <span className="text-sm">
                    {r.week_start} · <span className="font-serif text-lg text-terra">€ {Number(r.price).toLocaleString("it-IT")}</span>
                  </span>
                  <button
                    data-testid={`admin-rate-delete-${r.id}`}
                    onClick={() => delRate(r.id)}
                    aria-label="Elimina tariffa"
                    className="grid h-8 w-8 place-items-center rounded-full border border-sand/20 text-sand/70 transition-colors duration-300 hover:border-terra hover:text-terra"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="lg:col-span-3">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl">
              <ImageIcon className="h-5 w-5 text-terra" />
              Foto galleria ({photos.length})
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-sand/10 bg-ink2/25 p-5" data-testid="admin-upload-card">
                <label className="text-xs uppercase tracking-[0.2em] text-sand/60">Carica una foto</label>
                <input
                  data-testid="admin-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={uploadPhoto}
                  disabled={uploading}
                  className="mt-3 block w-full text-sm text-sand/70 file:mr-3 file:rounded-full file:border-0 file:bg-terra file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-widest file:text-sand hover:file:bg-olive"
                />
                {uploading && <Loader2 className="mt-3 h-4 w-4 animate-spin text-terra" />}
              </div>
              <form onSubmit={genImage} className="rounded-lg border border-sand/10 bg-ink2/25 p-5" data-testid="admin-ai-image-card">
                <label className="text-xs uppercase tracking-[0.2em] text-sand/60">Genera con l'AI</label>
                <input
                  data-testid="admin-ai-prompt"
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="es. tramonto sulla piscina tra le palme"
                  className={input + " mt-3"}
                />
                <button
                  type="submit"
                  data-testid="admin-ai-generate"
                  disabled={generating || !aiPrompt.trim()}
                  className="mt-3 flex h-10 items-center gap-2 rounded-full bg-olive px-5 text-xs font-semibold uppercase tracking-[0.14em] text-sand transition-colors duration-300 hover:bg-terra disabled:opacity-60"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Genera immagine
                </button>
              </form>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="admin-photos-list">
              {photos.map((p, idx) => (
                <div key={p.id} className="group relative overflow-hidden rounded-md border border-sand/10">
                  <img src={p.url} alt={p.alt} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  {p.source === "ai" && (
                    <span className="absolute left-2 top-2 rounded-full bg-ink/80 px-2 py-0.5 text-[9px] uppercase tracking-widest text-sand">AI</span>
                  )}
                  <div className="absolute left-1.5 top-1.5 flex flex-col gap-1 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <button
                      data-testid={`admin-photo-up-${p.id}`}
                      onClick={() => movePhoto(p.id, -1)}
                      disabled={idx === 0}
                      aria-label="Sposta su"
                      className="grid h-6 w-6 place-items-center rounded-full bg-ink/85 text-sand/90 transition-colors duration-300 hover:bg-olive disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      data-testid={`admin-photo-down-${p.id}`}
                      onClick={() => movePhoto(p.id, 1)}
                      disabled={idx === photos.length - 1}
                      aria-label="Sposta giù"
                      className="grid h-6 w-6 place-items-center rounded-full bg-ink/85 text-sand/90 transition-colors duration-300 hover:bg-olive disabled:opacity-30"
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    data-testid={`admin-photo-delete-${p.id}`}
                    onClick={() => delPhoto(p.id)}
                    aria-label="Elimina foto"
                    className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-ink/80 text-sand/80 opacity-0 transition-opacity duration-300 hover:text-terra group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {photos.length === 0 && (
                <p className="col-span-full text-sm text-sand/50">Nessuna foto extra: la galleria mostra quelle base.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
